import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { patchJob } from '../api/client.js';
import { qk } from '../lib/queryKeys.js';
import { Input } from './ui/input.js';

// Always-editable job name. Used in two contexts:
//   - JobDetailPage breadcrumb (variant="breadcrumb")
//   - JobDetailPanel SheetTitle (variant="title")
// The job name has no status gate — the backend PATCH /api/jobs/:id accepts
// displayName in any status. Saves on blur/Enter; empty draft is rejected
// client-side (schema requires min(1)) and reverts to the stored value.
//
// Escape has a DIFFERENT consumer in each context: on the page it bubbles to
// JobDetailPage's window-Escape → /jobs nav; in the drawer it hits Radix
// Sheet's onEscapeKeyDown → closes the whole drawer. So the input's onKeyDown
// stops propagation on Escape and cancels the edit locally in BOTH places.
export function InlineJobNameEditor({
  jobId,
  value,
  variant = 'breadcrumb',
  onSaved,
}: {
  jobId: string;
  value: string;
  variant?: 'breadcrumb' | 'title';
  onSaved?: () => void;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Re-seed the draft when the stored value changes (refetch after save, or
  // navigating between jobs while this component stays mounted).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const mutation = useMutation({
    mutationFn: (displayName: string) => patchJob(jobId, { displayName }),
    onSuccess: () => {
      // qk.jobs.detail === ['job', id] — the SAME key the page AND the
      // sidecard panel read, so one invalidation refreshes both. Plus the
      // jobs list / today card / calendar pills that render the name.
      void queryClient.invalidateQueries({ queryKey: qk.jobs.detail(jobId) });
      void queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.today() });
      void queryClient.invalidateQueries({ queryKey: ['calendar', 'assignments'] });
      onSaved?.();
    },
    onError: (err) => {
      setDraft(value);
      alert(err instanceof Error ? err.message : 'Failed to rename job');
    },
  });

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === '' || trimmed === value) {
      setDraft(value); // empty would 400 (min 1); no-op if unchanged
      setEditing(false);
      return;
    }
    mutation.mutate(trimmed);
    setEditing(false);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={draft}
        maxLength={200}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            // Cancel the edit, and do NOT let Escape reach the page-nav
            // listener (window) or the Radix Sheet's onEscapeKeyDown.
            e.stopPropagation();
            cancel();
          }
        }}
        disabled={mutation.isPending}
        className={variant === 'title' ? 'h-8 text-lg font-semibold' : 'h-7 w-64 text-sm'}
      />
    );
  }

  return (
    <span className="group inline-flex items-center gap-1.5">
      <span className={variant === 'title' ? 'font-semibold text-foreground' : 'font-medium text-foreground'}>
        {value}
      </span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        title="Rename job"
        aria-label="Rename job"
        className="rounded p-0.5 text-muted-foreground opacity-60 transition-opacity hover:text-foreground hover:opacity-100"
      >
        <Pencil className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}
